import {
  Box,
  Button,
  IconButton,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  blackShade05,
  blackShade19,
  blueColor04,
  border25,
  borderTextBoxColor,
  greenColor,
  greyColor42,
  greyColor47,
  greyShade555,
  lightRedColor,
  mobilePrimary,
  redColor,
  whiteColor,
} from "../../../configs/styles/muiThemes";
import TabelCellInputBox from "../../../components/TableInputs/TabelCellInputBox";
import ImageComponent from "../../AddProductVariant/Component/ImageComponent";
import DescriptionInput from "../../CatalogBulkUploadNewCopy/Components/Popups/DescriptionInput";
import { textOneLines } from "../../../utils/styles";
import OndcSpecInput from "../../CatalogBulkUploadNewCopy/Components/OndcSpecInput/OndcSpecInput";
import { toTitleCase } from "../../../utils/validationUtils";
import { dynamicSort } from "../../../utils/helper";
import { useSelector } from "react-redux";
import TableCellDropdownBox from "../../../components/TableInputs/TableCellDropdownBox";

function ProductVariantForm({
  tableHeader,
  variantData,
  handleValueChange,
  handleAddRow,
  onCopyClick,
  onDeleteClick,
  onDeletePhotos,
  productCodeList,
}) {
  const { gstRates } = useSelector((state) => state.catalog);

  return (
    <Box sx={styles.card}>
      <Typography sx={styles.title}>
        Variance Details - {variantData?.length} Variants
      </Typography>

      <Box sx={styles.tableWrapper}>
        <TableContainer sx={styles.tableContainer} component={Box}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow
                sx={{
                  position: "sticky !important",
                  top: 0,
                  backgroundColor: greyColor42,
                  zIndex: 20,
                  borderRadius: "8px 8px 0 0",
                }}
              >
                {tableHeader?.map((item, index) => {
                  return (
                    <TableCell
                      key={item.id}
                      sx={{
                        ...styles.tableCell,
                        ...styles.colSticky(
                          variantData?.[0]?.variant2,
                          variantData?.[0]?.variant3
                        ),
                      }}
                    >
                      <Box
                        display={index === 0 ? "flex" : "inline-block"}
                        alignItems="center"
                        gap={1.2}
                      >
                        <Box sx={{ fontSize: 12, textTransform: "uppercase" }}>
                          {item?.title}{" "}
                          {item?.isRequired && (
                            <span style={{ color: "red" }}>*</span>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody sx={{ backgroundColor: whiteColor }}>
              {variantData?.map((data, index) => (
                <TableRow
                  key={data?.variant1 + index}
                  sx={{
                    borderBottom: `1px solid ${borderTextBoxColor}`,
                    verticalAlign: "top",
                  }}
                >
                  <TableCell sx={styles.tableMainSticky}>
                    <Box
                      minWidth="50px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={0.5}
                    >
                      <Radio
                        checked={data?.main}
                        onChange={() => {
                          handleValueChange("main", true, index);
                        }}
                        sx={styles.checkbox}
                      />
                    </Box>
                  </TableCell>
                  {data?.productId ? (
                    <TableCell
                      sx={styles.tableStickyLeftCell(data?.errors?.variant1)}
                    >
                      <Typography
                        variant="body1"
                        title={data?.variant1?.inputValue}
                        sx={{ ...styles.prodNameBox, ...textOneLines }}
                      >
                        {data?.variant1?.type === "codeValue"
                          ? toTitleCase(data?.variant1?.inputValue)
                          : data?.variant1?.inputValue}
                      </Typography>
                    </TableCell>
                  ) : (
                    <OndcSpecInput
                      key={`spec_data${1}`}
                      value={data?.variant1?.inputValue}
                      specItemInfo={data?.variant1}
                      // disabled
                      errorMsg={data?.errors?.variant1}
                      customStyles={{
                        ...styles.tableStickyOndcLeftCell,
                      }}
                      width="120px"
                      onChange={(value, selector) => {
                        handleValueChange("variant1", value, index);
                      }}
                    />
                  )}
                  {data?.variant2 ? (
                    data?.productId ? (
                      <TableCell
                        sx={styles.tableStickyLeft1Cell(data?.errors?.variant2)}
                      >
                        <Typography
                          variant="body1"
                          title={data?.variant1?.inputValue}
                          sx={{ ...styles.prodNameBox, ...textOneLines }}
                        >
                          {data?.variant2?.type === "codeValue"
                            ? toTitleCase(data?.variant2?.inputValue)
                            : data?.variant2?.inputValue}
                        </Typography>
                      </TableCell>
                    ) : (
                      <OndcSpecInput
                        key={`spec_data${2}`}
                        value={data?.variant2?.inputValue}
                        specItemInfo={data?.variant2}
                        width="120px"
                        // disabled
                        errorMsg={data?.errors?.variant2}
                        customStyles={{
                          ...styles.tableStickyOndcLeft1Cell,
                        }}
                        onChange={(value, selector) => {
                          handleValueChange("variant2", value, index);
                        }}
                      />
                    )
                  ) : null}

                  {data?.variant3 ? (
                    data?.productId ? (
                      <TableCell
                        sx={styles.tableStickyLeft2Cell(data?.errors?.variant3)}
                      >
                        <Typography
                          variant="body1"
                          title={data?.variant1?.inputValue}
                          sx={{ ...styles.prodNameBox, ...textOneLines }}
                        >
                          {data?.variant3?.type === "codeValue"
                            ? toTitleCase(data?.variant3?.inputValue)
                            : data?.variant3?.inputValue}
                        </Typography>
                      </TableCell>
                    ) : (
                      <OndcSpecInput
                        key={`spec_data${3}`}
                        value={data?.variant3?.inputValue}
                        specItemInfo={data?.variant3}
                        width="120px"
                        errorMsg={data?.errors?.variant3}
                        onChange={(value, selector) => {
                          handleValueChange("variant3", value, index);
                        }}
                        customStyles={{
                          ...styles.tableStickyOndcLeft2Cell,
                        }}
                      />
                    )
                  ) : null}

                  <TableCell
                    sx={styles.tableBodyCell(data?.errors?.description)}
                  >
                    <Box sx={styles.prodDescriptionBox}>
                      <DescriptionInput
                        description={data?.description}
                        onChange={(value) => {
                          handleValueChange("description", value, index);
                        }}
                        errorMsg={data?.errors?.description}
                      />
                    </Box>
                  </TableCell>

                  <TableCell sx={styles.tableBodyCell(false)}>
                    <TabelCellInputBox
                      value={data?.skuId}
                      onValueChange={(value) => {
                        handleValueChange("skuId", value, index);
                      }}
                      minWidth="100px"
                    />
                  </TableCell>

                  <TableCell
                    sx={styles.tableBodyCell(data?.errors?.gstPercentage)}
                  >
                    <TableCellDropdownBox
                      value={data?.gstPercentage}
                      onValueChange={(value) => {
                        handleValueChange("gstPercentage", value?.value, index);
                      }}
                      returnFullItem
                      menuList={[...gstRates]}
                      valueSelector="value"
                      borderFull
                      rounded
                      errorMsg={data?.errors?.gstPercentage}
                      width="140px"
                    />
                  </TableCell>
                  {console.log(
                    "productCodeListproductCodeList",
                    productCodeList
                  )}

                  <TableCell
                    sx={styles.tableBodyCell(data?.errors?.productCodeKey)}
                  >
                    <TableCellDropdownBox
                      value={data?.productCodeKey}
                      onValueChange={(value) => {
                        console.log("productCodeKey", value);
                        handleValueChange("productCodeKey", value, index);
                      }}
                      valueSelector="prodictCode"
                      menuList={
                        productCodeList
                          ?.slice()
                          ?.sort(dynamicSort("prodictCode")) || []
                      }
                      borderFull
                      rounded
                      errorMsg={data?.errors?.productCodeKey}
                      width="100px"
                    />
                  </TableCell>

                  <TableCell
                    sx={styles.tableBodyCell(data?.errors?.productCodeValue)}
                  >
                    <TabelCellInputBox
                      value={data?.productCodeValue}
                      onValueChange={(value) => {
                        handleValueChange("productCodeValue", value, index);
                      }}
                      regexType="number"
                      maxLength={13}
                      disabled={!data?.productCodeKey}
                      errorMsg={data?.errors?.productCodeValue}
                    />
                  </TableCell>

                  <TableCell
                    sx={styles.tableBodyCell(
                      data?.productId ? false : data?.errors?.originalPrice
                    )}
                  >
                    {data?.productId ? (
                      <Typography
                        variant="body1"
                        title={data?.originalPrice}
                        sx={{ ...styles.prodNameBox, ...textOneLines }}
                      >
                        {data?.originalPrice}
                      </Typography>
                    ) : (
                      <TabelCellInputBox
                        value={data?.originalPrice}
                        onValueChange={(value) => {
                          handleValueChange("originalPrice", value, index);
                        }}
                        regexType="price"
                        errorMsg={data?.errors?.originalPrice}
                        minWidth="100px"
                      />
                    )}
                  </TableCell>

                  <TableCell
                    sx={styles.tableBodyCell(
                      data?.productId ? false : data?.errors?.offerPrice
                    )}
                  >
                    {data?.productId ? (
                      <Typography
                        variant="body1"
                        title={data?.offerPrice}
                        sx={{ ...styles.prodNameBox, ...textOneLines }}
                      >
                        {data?.offerPrice}
                      </Typography>
                    ) : (
                      <TabelCellInputBox
                        value={data?.offerPrice}
                        onValueChange={(value) => {
                          handleValueChange("offerPrice", value, index);
                        }}
                        regexType="price"
                        errorMsg={data?.errors?.offerPrice}
                        minWidth="100px"
                      />
                    )}
                  </TableCell>

                  <TableCell sx={styles.tableBodyCell(data?.errors?.photo1)}>
                    <ImageComponent
                      value={data?.photo1}
                      onValueChange={(value) =>
                        handleValueChange("photo1", value, index)
                      }
                      onDeletePhotos={() => onDeletePhotos("photo1", index)}
                      errorMsg={data?.errors?.photo1}
                    />
                  </TableCell>

                  <TableCell sx={styles.tableBodyCell(false)}>
                    <ImageComponent
                      value={data?.photo2}
                      onValueChange={(value) =>
                        handleValueChange("photo2", value, index)
                      }
                      onDeletePhotos={() => onDeletePhotos("photo2", index)}
                    />
                  </TableCell>

                  <TableCell sx={styles.tableBodyCell(false)}>
                    <ImageComponent
                      value={data?.photo3}
                      onValueChange={(value) =>
                        handleValueChange("photo3", value, index)
                      }
                      onDeletePhotos={() => onDeletePhotos("photo3", index)}
                    />
                  </TableCell>

                  <TableCell sx={styles.tableBodyCell(false)}>
                    <ImageComponent
                      value={data?.photo4}
                      onValueChange={(value) =>
                        handleValueChange("photo4", value, index)
                      }
                      onDeletePhotos={() => onDeletePhotos("photo4", index)}
                    />
                  </TableCell>

                  <TableCell
                    sx={styles.tableBodyCell(data?.errors?.productWeight)}
                  >
                    <TabelCellInputBox
                      value={data?.productWeight}
                      onValueChange={(value) => {
                        handleValueChange("productWeight", value, index);
                      }}
                      regexType="weight"
                      errorMsg={data?.errors?.productWeight}
                    />
                  </TableCell>

                  <TableCell
                    sx={styles.tableBodyCell(data?.errors?.netProductWeight)}
                  >
                    <TabelCellInputBox
                      value={data?.netProductWeight}
                      onValueChange={(value) => {
                        handleValueChange("netProductWeight", value, index);
                      }}
                      regexType="weight"
                      errorMsg={data?.errors?.netProductWeight}
                    />
                  </TableCell>

                  <TableCell sx={styles.tableBodyCell(false)}>
                    <TabelCellInputBox
                      value={data?.productLength}
                      onValueChange={(value) => {
                        handleValueChange("productLength", value, index);
                      }}
                      regexType="dimension"
                      // disabled={!isOndcEnabled || !isCancellable}
                      // errorMsg={
                      //   productInfo?.ondcErrors?.cancellationFeePercentage
                      // }
                    />
                  </TableCell>

                  <TableCell sx={styles.tableBodyCell(false)}>
                    <TabelCellInputBox
                      value={data?.productHeight}
                      onValueChange={(value) => {
                        handleValueChange("productHeight", value, index);
                      }}
                      regexType="dimension"
                      // disabled={!isOndcEnabled || !isCancellable}
                      // errorMsg={
                      //   productInfo?.ondcErrors?.cancellationFeePercentage
                      // }
                    />
                  </TableCell>

                  <TableCell sx={styles.tableBodyCell(false)}>
                    <TabelCellInputBox
                      value={data?.productWidth}
                      onValueChange={(value) => {
                        handleValueChange("productWidth", value, index);
                      }}
                      regexType="dimension"
                      // disabled={!isOndcEnabled || !isCancellable}
                      // errorMsg={
                      //   productInfo?.ondcErrors?.cancellationFeePercentage
                      // }
                    />
                  </TableCell>

                  <TableCell
                    sx={styles.tableBodyCell(
                      data?.productId ? false : data?.errors?.qty
                    )}
                  >
                    {data?.productId ? (
                      <Typography
                        variant="body1"
                        title={data?.qty}
                        sx={{ ...styles.prodNameBox, ...textOneLines }}
                      >
                        {data?.qty}
                      </Typography>
                    ) : (
                      <TabelCellInputBox
                        value={data?.qty}
                        onValueChange={(value) => {
                          handleValueChange("qty", value, index);
                        }}
                        regexType="qty"
                        errorMsg={data?.errors?.qty}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={styles.tableBodyCell(
                      data?.productId ? false : data?.errors?.alertQty
                    )}
                  >
                    {data?.productId ? (
                      <Typography
                        variant="body1"
                        title={data?.alertQty}
                        sx={{ ...styles.prodNameBox, ...textOneLines }}
                      >
                        {data?.alertQty}
                      </Typography>
                    ) : (
                      <TabelCellInputBox
                        value={data?.alertQty}
                        onValueChange={(value) => {
                          handleValueChange("alertQty", value, index);
                        }}
                        regexType="qty"
                        errorMsg={data?.errors?.alertQty}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={styles.tableBodyCell(
                      data?.productId ? false : data?.errors?.minQty
                    )}
                  >
                    {data?.productId ? (
                      <Typography
                        variant="body1"
                        title={data?.minQty}
                        sx={{ ...styles.prodNameBox, ...textOneLines }}
                      >
                        {data?.minQty}
                      </Typography>
                    ) : (
                      <TabelCellInputBox
                        value={data?.minQty}
                        onValueChange={(value) => {
                          handleValueChange("minQty", value, index);
                        }}
                        regexType="qty"
                        errorMsg={data?.errors?.minQty}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={styles.tableBodyCell(
                      data?.productId ? false : data?.errors?.maxQty
                    )}
                  >
                    {data?.productId ? (
                      <Typography
                        variant="body1"
                        title={data?.maxQty}
                        sx={{ ...styles.prodNameBox, ...textOneLines }}
                      >
                        {data?.maxQty}
                      </Typography>
                    ) : (
                      <TabelCellInputBox
                        value={data?.maxQty}
                        onValueChange={(value) => {
                          handleValueChange("maxQty", value, index);
                        }}
                        regexType="qty"
                        errorMsg={data?.errors?.maxQty}
                      />
                    )}
                  </TableCell>

                  <TableCell
                    sx={{
                      // ...styles.tableStickyLeftCell,
                      ...styles.tableStickyRightCell,
                    }}
                  >
                    <Box
                      minWidth="50px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={0.5}
                    >
                      <Stack alignItems="center" justifyContent="center" mr={1}>
                        {data?.errors &&
                        Object.keys(data?.errors).length > 0 ? (
                          <Box sx={styles.errorCountTag}>
                            {Object.keys(data?.errors).length}
                          </Box>
                        ) : (
                          <CheckCircleIcon
                            sx={{ fontSize: 16, color: greenColor }}
                          />
                        )}
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={() => onCopyClick(data)}
                      >
                        <ContentCopyRoundedIcon
                          sx={{ fontSize: "16px", color: greyShade555 }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onDeleteClick(index)}
                      >
                        <DeleteIcon
                          sx={{ fontSize: "16px", color: redColor }}
                        />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box display="flex" justifyContent={"flex-end"} mt={1}>
        <Button sx={styles.actionBtn} onClick={handleAddRow}>
          Add Row
        </Button>
      </Box>
    </Box>
  );
}

export default ProductVariantForm;

const styles = {
  card: {
    backgroundColor: whiteColor,
    borderRadius: "13px",
    border: border25,
    boxShadow: "0 3px 4px rgb(229 233 240 / 22%)",
    p: "15px 15px 15px",
    mb: 2,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "20px",
    mb: "10px",
    color: blackShade19,
  },
  actionBtn: {
    backgroundColor: { md: blueColor04, xs: mobilePrimary },
    padding: "2px 10px",
    fontSize: "12px",
    fontWeight: 500,
    color: whiteColor,
    border: "1px solid " + { md: blueColor04, xs: mobilePrimary },
    "&:hover": {
      color: { md: blueColor04, xs: mobilePrimary },
      backgroundColor: whiteColor,
    },
  },
  tableWrapper: {
    width: { md: "calc(100vw - 310px)", xs: "100%" },
    borderRadius: "8px",
    border: `1px solid ${borderTextBoxColor}`,
  },

  tableContainer: {
    borderRadius: "8px",
    overflow: "scroll",
    maxHeight: "calc(100vh - 370px)",
    maxWidth: "100%",
    "&::-webkit-scrollbar": {
      width: "4px",
      height: "4px",
      borderRadius: "50px !important",
    },
    "&::-webkit-scrollbar-thumb": {
      // background: '#d0d3da',
      background: "rgba(0,0,0,0.1)",
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(0,0,0,0.05)",
    },
  },

  tableCell: {
    color: greyColor47,
    fontSize: "12.5px",
    fontWeight: "500",
    whiteSpace: "nowrap",
    padding: "8px 15px",
    backgroundColor: greyColor42,
    borderRight: `1px solid ${whiteColor}`,
  },

  colSticky: (sticky1, sticky2) => ({
    ":first-child": {
      position: "sticky",
      left: "0px",
    },
    ":nth-child(2)": {
      position: "sticky",
      left: "80px",
      boxShadow: `-1px 0 0 0 ${whiteColor} inset ,1px 0 0 0 ${whiteColor} inset`,
    },
    ":nth-child(3)": sticky1
      ? {
          position: "sticky",
          left: "230px",
          boxShadow: `-1px 0 0 0 ${whiteColor} inset ,1px 0 0 0 ${whiteColor} inset`,
        }
      : "",
    ":nth-child(4)": sticky2
      ? {
          position: "sticky",
          left: "380px",
          boxShadow: `-1px 0 0 0 ${whiteColor} inset ,1px 0 0 0 ${whiteColor} inset`,
        }
      : "",

    ":last-child": { position: "sticky", right: "0px" },
    boxShadow: `-1px 0 0 0 ${borderTextBoxColor} inset `,
  }),

  statuSticky: (isStatus) => ({
    right: isStatus ? "100px" : "unset",
  }),

  colStickyRight: (isSticky) => ({
    // position: "-webkit-sticky",
    position: isSticky ? "sticky" : "unset",
    width: isSticky ? "100px" : "auto",
    minWidth: isSticky ? "100px" : "auto",
    maxWidth: isSticky ? "100px" : "auto",
    right: "0px",
    boxShadow: `-1px 0 0 0 ${borderTextBoxColor} inset`,
  }),

  tableBodyCell: (isError) => ({
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: "nowrap",
    color: blackShade05,
    backgroundColor: isError ? lightRedColor : whiteColor,
    padding: "8px 15px !important",
    borderRight: `1px solid ${borderTextBoxColor}`,

    boxShadow: isError ? `0px -1px 0 0 ${redColor} inset` : "none",
  }),

  tableStickyLeftCell: (isError) => ({
    minWidth: "150px !important",
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: "nowrap",
    color: blackShade05,
    backgroundColor: isError ? lightRedColor : whiteColor,
    padding: "10px 15px !important",
    position: "sticky",
    left: "80px",
    zIndex: 12,
    boxShadow: `-1px 0 0 0 ${borderTextBoxColor} inset ,1px 0 0 0 ${borderTextBoxColor} inset`,
  }),
  tableStickyLeft1Cell: (isError) => ({
    minWidth: "150px !important",
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: "nowrap",
    color: blackShade05,
    backgroundColor: isError ? lightRedColor : whiteColor,
    padding: "10px 15px !important",
    position: "sticky",
    left: "230px",
    zIndex: 12,
    boxShadow: `-1px 0 0 0 ${borderTextBoxColor} inset ,1px 0 0 0 ${borderTextBoxColor} inset`,
  }),
  tableStickyLeft2Cell: (isError) => ({
    minWidth: "150px !important",
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: "nowrap",
    color: blackShade05,
    backgroundColor: isError ? lightRedColor : whiteColor,
    padding: "10px 15px !important",
    position: "sticky",
    left: "380px",
    zIndex: 12,
    boxShadow: `-1px 0 0 0 ${borderTextBoxColor} inset ,1px 0 0 0 ${borderTextBoxColor} inset`,
  }),

  tableStickyOndcLeftCell: {
    position: "sticky",
    left: "80px",
    zIndex: 12,
    boxShadow: `-1px 0 0 0 ${borderTextBoxColor} inset,1px 0 0 0 ${borderTextBoxColor} inset`,
  },
  tableStickyOndcLeft1Cell: {
    position: "sticky",
    left: "230px",
    zIndex: 12,
    boxShadow: `-1px 0 0 0 ${borderTextBoxColor} inset ,1px 0 0 0 ${borderTextBoxColor} inset`,
  },
  tableStickyOndcLeft2Cell: {
    position: "sticky",
    left: "380px",
    zIndex: 12,
    boxShadow: `-1px 0 0 0 ${borderTextBoxColor} inset ,1px 0 0 0 ${borderTextBoxColor} inset`,
  },

  tableStickyRightCell: {
    position: "sticky",
    right: 0,
    zIndex: 12,
    boxShadow: `1px 0 0 0 ${borderTextBoxColor} inset`,
    backgroundColor: whiteColor,
  },
  tableMainSticky: {
    minWidth: "80px !important",
    backgroundColor: whiteColor,
    position: "sticky",
    left: "0px",
    zIndex: 12,
    boxShadow: `-1px 0 0 0 ${borderTextBoxColor} inset`,
  },

  deleteBtn: {
    fontSize: 13,
    fontWeight: 500,
    color: whiteColor,
    p: "4px 30px",
    border: `1.5px solid ${redColor}`,
    backgroundColor: redColor,
    "&:hover": {
      color: redColor,
      backgroundColor: whiteColor,
    },
  },
  checkbox: {
    p: 0,
    transform: "scale(1.3)",
    "&.Mui-checked": {
      color: blueColor04,
    },
  },
  errorCountTag: {
    backgroundColor: lightRedColor,
    color: redColor,
    fontSize: 12,
    fontWeight: 600,
    p: "3px 8px",
    borderRadius: "10px",
  },
  prodDescriptionBox: {
    minWidth: "350px",
    maxWidth: "550px",
    whiteSpace: "normal",
  },
  prodNameBox: {
    minWidth: "120px",
    maxWidth: "120px",
    whiteSpace: "normal",
    fontSize: 12,
    fontWeight: 600,
    color: blackShade19,
    lineHeight: "16px",
  },
};
